import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Github, Mail, ExternalLink, Menu, X, Code2, Cpu, Sparkles, Terminal } from 'lucide-react';
import { projects, skills } from './data';
import './App.css';

const Portfolio = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [terminalLines, setTerminalLines] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);

  // Terminal loading sequence
  useEffect(() => {
    const lines = [
      { text: '> Initializing portfolio...', delay: 0 },
      { text: '> Booting backend services...', delay: 600 },
      { text: '> Fetching GitHub repositories: success.', delay: 1200 },
      { text: '> Authenticating moaljabri@dev: verified.', delay: 1800 },
      { text: '> Querying /goals...', delay: 2400 },
      { text: '> Found: Become advanced backend dev.', delay: 3000 },
      { text: '> Querying /mindset...', delay: 3600 },
      { text: '> Found: always building, always improving.', delay: 4200 },
      { text: '> Ready! Welcome to my dimension.', delay: 4800 },
    ];

    const timeouts = [];
    lines.forEach((line, index) => {
      const timeout = setTimeout(() => {
        setTerminalLines(prev => [...prev, { text: line.text, id: index }]);
      }, line.delay);
      timeouts.push(timeout);
    });

    const fadeTimeout = setTimeout(() => setFadeIn(true), 5000);
    const loadTimeout = setTimeout(() => setIsLoading(false), 5300);

    return () => {
      timeouts.forEach(t => clearTimeout(t));
      clearTimeout(fadeTimeout);
      clearTimeout(loadTimeout);
    };
  }, []);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check URL for project routing and get current project
  const [currentProject, setCurrentProject] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  
  useEffect(() => {
    const path = window.location.pathname;
    const projectName = path.split('/').filter(Boolean).pop();
    
    if (projectName && projectName !== '') {
      const project = projects.find(p => 
        p.name.toLowerCase() === projectName.toLowerCase()
      );
      
      if (project) {
        setCurrentProject(project);
        setRedirecting(true);
        
        // Redirect after showing preview
        setTimeout(() => {
          window.location.href = project.url;
        }, 2000);
      }
    }
  }, []);

  // Show redirecting screen if navigating to project
  if (redirecting && currentProject) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex items-center justify-center z-50">
        <div className="w-full max-w-2xl px-6">
          <div className="bg-gray-800 rounded-lg border border-yellow-500/30 shadow-2xl shadow-yellow-500/20 overflow-hidden animate-pulse">
            <div className="bg-gray-900 px-4 py-3 flex items-center gap-2 border-b border-yellow-500/30">
              <ExternalLink className="text-yellow-400" size={18} />
              <span className="text-yellow-400 font-mono text-sm">Redirecting to {currentProject.name}...</span>
            </div>
            
            <div className="p-8 text-center">
              <div className="mb-4">
                {React.cloneElement(currentProject.icon, { 
                  size: 64, 
                  className: "text-yellow-400 mx-auto animate-bounce" 
                })}
              </div>
              <h2 className="text-2xl font-bold text-yellow-400 mb-2">{currentProject.name}</h2>
              <p className="text-gray-400 mb-4">{currentProject.description}</p>
              <p className="text-sm text-gray-500">Taking you to the project in a moment...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Terminal Loading Screen
  if (isLoading) {
    return (
      <div className={`fixed inset-0 bg-gray-900 flex items-center justify-center z-50 transition-opacity duration-500 ${fadeIn ? 'opacity-0' : 'opacity-100'}`}>
        <div className="w-full max-w-2xl px-6">
          <div className="bg-gray-800 rounded-lg border border-yellow-500/30 shadow-2xl shadow-yellow-500/20 overflow-hidden">
            <div className="bg-gray-900 px-4 py-2 flex items-center gap-2 border-b border-yellow-500/30">
              <Terminal className="text-yellow-400" size={18} />
              <span className="text-yellow-400 font-mono text-sm">moaljabri@portfolio:~$</span>
            </div>
            
            <div className="p-6 font-mono text-sm space-y-2 min-h-[200px]">
              {terminalLines.map((line) => (
                <div 
                  key={line.id} 
                  className="terminal-line text-green-400"
                >
                  {line.text}
                </div>
              ))}
              <div className="inline-block w-2 h-4 bg-yellow-400 animate-pulse ml-1"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Dynamic Open Graph Meta Tags */}
      <Helmet>
        
        <title>
          {currentProject 
            ? `${currentProject.name} | Mohammed` 
            : "Mohammed | Backend Developer Portfolio"}
        </title>
        <meta 
          name="description" 
          content={currentProject 
            ? currentProject.description 
            : "20-year-old junior backend developer focused on building scalable APIs and reliable server-side using Node.js and Express."} 
        />
        
        {/* Open Graph */}
        <meta 
          property="og:title" 
          content={currentProject 
            ? `${currentProject.name}` 
            : "Mohammed | Backend Developer"} 
        />
        <meta 
          property="og:description" 
          content={currentProject 
            ? currentProject.description 
            : "Junior backend developer specializing in Node.js, Express, and REST APIs. Check out my portfolio!"} 
        />
        <meta 
          property="og:image" 
          content={currentProject && currentProject.image 
            ? currentProject.image 
            : "https://i.ibb.co/PG8FWXzL/og-moaljabri-dev.png"} 
        />
        <meta 
          property="og:url" 
          content={currentProject 
            ? `https://moaljabri-dev.onrender.com/${currentProject.name}` 
            : "https://moaljabri-dev.onrender.com"} 
        />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta 
          name="twitter:title" 
          content={currentProject 
            ? `${currentProject.name} | Mohammed` 
            : "Mohammed | Backend Developer"} 
        />
        <meta 
          name="twitter:description" 
          content={currentProject 
            ? currentProject.description 
            : "Junior backend developer specializing in Node.js, Express, and REST APIs"} 
        />
        <meta 
          name="twitter:image" 
          content={currentProject && currentProject.image 
            ? currentProject.image 
            : "https://i.ibb.co/Mx4jGY2g/moaljabri-dev.png"} 
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 text-white relative overflow-hidden">
        {/* Animated background effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        {/* Mobile menu button */}
        {isMobile && (
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="fixed top-4 right-4 z-50 p-2 bg-gray-800/80 backdrop-blur-sm rounded-lg border border-yellow-500/30 hover:border-yellow-500 transition-all transform hover:scale-110 active:scale-95"
            style={{ animation: 'slideInRight 0.5s ease-out' }}
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}

        {/* Sidebar */}
        <aside
          className={`fixed top-0 right-0 h-full bg-gray-900/95 backdrop-blur-md border-l border-yellow-500/30 z-40 transition-transform duration-300 ${
            isMobile ? (isSidebarOpen ? 'translate-x-0' : 'translate-x-full') : 'translate-x-0'
          } w-80 p-6 overflow-y-auto`}
          style={{ animation: isMobile ? 'none' : 'slideInRight 0.6s ease-out' }}
        >
          <div className="mb-8" style={{ animation: 'fadeInUp 0.8s ease-out' }}>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-cyan-400 bg-clip-text text-transparent mb-2">
              Featured Projects
            </h3>
            <div className="h-1 w-16 bg-gradient-to-r from-yellow-400 to-cyan-400 rounded" style={{ animation: 'expandWidth 0.8s ease-out' }}></div>
          </div>

          <div className="space-y-4">
            {projects.map((project, idx) => (
              <div
                key={idx}
                className="group bg-gray-800/50 backdrop-blur rounded-xl p-4 border border-yellow-500/20 hover:border-yellow-500/50 transition-all hover:shadow-lg hover:shadow-yellow-500/20 cursor-pointer transform hover:-translate-y-1 hover:scale-105"
                style={{ animation: `fadeInUp 0.6s ease-out ${0.2 + idx * 0.1}s backwards` }}
              >
                <div className="flex items-start gap-3 mb-2">
                  {/* Icons with yellow glow on hover */}
                  <div className="text-yellow-400 transform group-hover:scale-110 transition-all group-hover:drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]">
                    {React.cloneElement(project.icon, { size: 28 })}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-yellow-400 group-hover:text-yellow-300 transition-colors">
                      {project.name}
                    </h4>
                    <p className="text-sm text-gray-400 mt-1">{project.description}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs bg-yellow-500/10 hover:bg-yellow-500/20 px-3 py-1.5 rounded-lg transition-all border border-yellow-500/30 transform hover:scale-105"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={12} />
                    Live
                  </a>
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs bg-cyan-500/10 hover:bg-cyan-500/20 px-3 py-1.5 rounded-lg transition-all border border-cyan-500/30 transform hover:scale-105"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Github size={12} />
                    Code
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-gradient-to-br from-yellow-500/10 to-cyan-500/10 rounded-xl border border-yellow-500/20" style={{ animation: 'fadeInUp 1s ease-out 0.8s backwards' }}>
            <p className="text-sm text-gray-300 mb-2">💡 Quick Access:</p>
            <p className="text-xs text-gray-400">
              Use <code className="bg-gray-800 px-1 py-0.5 rounded text-yellow-400">moaljabri-dev.onrender.com/ProjectName</code> to jump directly to any project!
            </p>
          </div>
        </aside>

        {/* Main content */}
        <main className={`min-h-screen transition-all duration-300 ${isMobile ? 'mr-0' : 'mr-80'} relative z-10`}>
          <div className="max-w-5xl mx-auto px-6 py-12 md:py-20">
            
            {/* Hero Section */}
            <section className="mb-16">
              <div className="inline-block mb-4 px-4 py-2 bg-yellow-500/10 rounded-full border border-yellow-500/30 transform hover:scale-105 transition-transform"
                style={{ animation: 'fadeInUp 0.6s ease-out' }}>
                <span className="text-yellow-400 text-sm font-medium">Backend Developer</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ animation: 'fadeInUp 0.6s ease-out 0.1s backwards' }}>
                <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-cyan-400 bg-clip-text text-transparent">
                  Mohammed Aljabri
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl" style={{ animation: 'fadeInUp 0.6s ease-out 0.2s backwards' }}>
                20-year-old junior backend developer focused on building scalable APIs and reliable server-side using Node.js and Express.
              </p>

              <div className="flex flex-wrap gap-4" style={{ animation: 'fadeInUp 0.6s ease-out 0.3s backwards' }}>
                <a
                  href="https://github.com/Hamood268"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 rounded-lg font-semibold transition-all shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/50 transform hover:scale-105 hover:-translate-y-1"
                >
                  <Github size={20} />
                  GitHub Profile
                </a>
                <a
                  href="mailto:haaoi.dev@gmail.com"
                  className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-semibold transition-all border border-cyan-500/30 hover:border-cyan-500 transform hover:scale-105 hover:-translate-y-1"
                >
                  <Mail size={20} />
                  Get in Touch
                </a>
              </div>
            </section>

            {/* Skills Grid */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6" style={{ animation: 'fadeInUp 0.6s ease-out 0.4s backwards' }}>
                <Cpu className="text-cyan-400" size={28} />
                <h2 className="text-3xl font-bold">Tech Stack</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {skills.map((skill, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-800/50 backdrop-blur px-4 py-3 rounded-lg border border-yellow-500/20 hover:border-yellow-500/50 transition-all text-center font-medium hover:shadow-lg hover:shadow-yellow-500/10 cursor-default transform hover:scale-105 hover:-translate-y-1"
                    style={{ animation: `fadeInUp 0.4s ease-out ${0.5 + idx * 0.05}s backwards` }}
                  >
                    {skill}
                  </div>
                ))}
              </div>
            </section>

            {/* About Section */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6" style={{ animation: 'fadeInUp 0.6s ease-out 1.2s backwards' }}>
                <Sparkles className="text-yellow-400" size={28} />
                <h2 className="text-3xl font-bold">What I Do</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-800/30 backdrop-blur p-6 rounded-xl border border-yellow-500/20 hover:border-yellow-500/40 transition-all transform hover:scale-105 hover:-translate-y-2" style={{ animation: 'fadeInUp 0.6s ease-out 1.3s backwards' }}>
                  <Code2 className="text-yellow-400 mb-3 transform hover:rotate-12 transition-transform" size={32} />
                  <h3 className="text-xl font-bold mb-3 text-yellow-400">Backend Development</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Developing REST APIs, handling database management, authentication, and server-side logic. Exploring microservices and improving system performance.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-800/30 backdrop-blur p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500/40 transition-all transform hover:scale-105 hover:-translate-y-2" style={{ animation: 'fadeInUp 0.6s ease-out 1.4s backwards' }}>
                  <Cpu className="text-cyan-400 mb-3 transform hover:rotate-12 transition-transform" size={32} />
                  <h3 className="text-xl font-bold mb-3 text-cyan-400">System Architecture</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Learning distributed systems, performance optimization, and reliable server architectures. Focused on practical backend solutions.
                  </p>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section style={{ animation: 'fadeInUp 0.6s ease-out 1.5s backwards' }}>
              <div className="bg-gradient-to-r from-yellow-500/10 via-cyan-500/10 to-yellow-500/10 backdrop-blur rounded-xl p-8 border border-yellow-500/20 transform hover:scale-105 transition-all">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div className="transform hover:scale-110 transition-transform">
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                      10+
                    </div>
                    <div className="text-gray-400 text-sm">Projects Built</div>
                  </div>
                  <div className="transform hover:scale-110 transition-transform">
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                      2+
                    </div>
                    <div className="text-gray-400 text-sm">Years Experience</div>
                  </div>
                  <div className="transform hover:scale-110 transition-transform">
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                      250+
                    </div>
                    <div className="text-gray-400 text-sm">GitHub Commits</div>
                  </div>
                  <div className="transform hover:scale-110 transition-transform">
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                      Flexible
                    </div>
                    <div className="text-gray-400 text-sm">Availability</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <footer className="mt-16 pt-8 border-t border-gray-800" style={{ animation: 'fadeInUp 0.6s ease-out 1.6s backwards' }}>
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-400 text-sm">
                <p>© 2026 Mohammed Aljabri</p>
                <div className="flex gap-4">
                  <a href="mailto:haaoi.dev@gmail.com" className="hover:text-yellow-400 transition-all transform hover:scale-110">
                    Email
                  </a>
                  <a href="https://github.com/Hamood268" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-all transform hover:scale-110">
                    GitHub
                  </a>
                  <a href="https://x.com/haaoi_dev" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-all transform hover:scale-110">
                    X
                  </a>
                  <a href="https://paypal.me/Mohammed0268" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-all transform hover:scale-110">
                    Donation
                  </a>
                  <a href="https://linktr.ee/haaoi" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-all transform hover:scale-110">
                    Socials
                  </a>
                </div>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default Portfolio;